import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BACKEND_URL } from '../constants';
import {
  Metadata,
  ProcessMeetingJobApi,
  RecordingDTO,
  RecordingsList,
  RenameRecording,
  Transcript,
} from '@axys-notes/common';
import * as jsondiffpatch from 'jsondiffpatch';
import { useTeamsContext } from '../providers/teams-context-provider';
import { handleErrorResponse, handleRetry } from './common';

export const useRecordMeetingMutation = () => {
  const teamsContext = useTeamsContext();
  return useMutation<void, Record<string, unknown>, ProcessMeetingJobApi>({
    mutationKey: ['search'],
    mutationFn: async (query: ProcessMeetingJobApi) => {
      const response = await fetch(`${BACKEND_URL}/api/recording`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: teamsContext.authCode
            ? `Bearer ${teamsContext.authCode}`
            : '',
        },
        body: JSON.stringify(query),
      });

      await handleErrorResponse(response, teamsContext);
      return;
    },
  });
};

export const useSearchRecordingsQuery = (
  searchQuery: string,
  page: number,
  folderId?: string
) => {
  const teamsContext = useTeamsContext();
  return useQuery<RecordingsList>({
    enabled:
      !!teamsContext.authCode &&
      teamsContext.consent &&
      !teamsContext.consentInProgress,
    queryKey: ['recording-list-get', searchQuery, page, folderId],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 10000,
    queryFn: async () => {
      const params: Record<string, string> = {
        searchQuery,
        page: String(page),
      };
      if (folderId) {
        params.folderId = folderId;
      }
      const response = await fetch(
        `${BACKEND_URL}/api/recording?${new URLSearchParams(params)}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
        }
      );
      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    retry: (failureCount: number, error: Error) =>
      handleRetry(failureCount, error, teamsContext),
  });
};

export const useGetMetadataQuery = () => {
  const teamsContext = useTeamsContext();
  return useQuery<Metadata>({
    enabled:
      !!teamsContext.authCode &&
      teamsContext.consent &&
      !teamsContext.consentInProgress,
    queryKey: ['metadata'],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 10000,
    queryFn: async () => {
      const response = await fetch(`${BACKEND_URL}/api/recording/metadata`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: teamsContext.authCode
            ? `Bearer ${teamsContext.authCode}`
            : '',
        },
      });
      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    retry: (failureCount: number, error: Error) =>
      handleRetry(failureCount, error, teamsContext),
  });
};

export const useGetRecordingQuery = (recordingId?: string) => {
  const teamsContext = useTeamsContext();
  return useQuery<RecordingDTO>({
    enabled:
      !!teamsContext.authCode &&
      teamsContext.consent &&
      !teamsContext.consentInProgress &&
      !!recordingId,
    queryKey: ['recording-get', recordingId],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: async () => {
      const response = await fetch(
        `${BACKEND_URL}/api/recording/${recordingId}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
        }
      );
      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    retry: (failureCount: number, error: Error) =>
      handleRetry(failureCount, error, teamsContext),
  });
};

export const useExportReportMutation = () => {
  const teamsContext = useTeamsContext();
  return useMutation<Blob, Record<string, unknown>, string>({
    mutationKey: ['search'],
    mutationFn: async (id: string) => {
      const response = await fetch(
        `${BACKEND_URL}/api/recording/${id}/report`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
        }
      );

      await handleErrorResponse(response, teamsContext);
      return response.blob();
    },
  });
};

export const useExportTranscriptMutation = () => {
  const teamsContext = useTeamsContext();
  return useMutation<Blob, Record<string, unknown>, string>({
    mutationKey: ['export-transcript'],
    mutationFn: async (id: string) => {
      const response = await fetch(
        `${BACKEND_URL}/api/recording/${id}/transcript-export`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
        }
      );

      await handleErrorResponse(response, teamsContext);
      return response.blob();
    },
  });
};

export const useRenameMeetingMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Record<string, unknown>,
    RenameRecording & { recordingId: string }
  >({
    mutationKey: ['search'],
    mutationFn: async (query) => {
      const { recordingId, ...dto } = query;
      const response = await fetch(
        `${BACKEND_URL}/api/recording/${recordingId}/name`,
        {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
          body: JSON.stringify(dto),
        }
      );

      await handleErrorResponse(response, teamsContext);
      return;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['recording-list-get'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['recording-get', variables.recordingId],
      });
    },
  });
};

export const usePatchTranscriptMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Record<string, unknown>,
    {
      recordingId: string;
      originalTranscript: Transcript;
      modifiedTranscript: Transcript;
    }
  >({
    mutationKey: ['patch-transcript'],
    mutationFn: async ({
      recordingId,
      originalTranscript,
      modifiedTranscript,
    }) => {
      // Calculer le diff
      const delta = jsondiffpatch.diff(originalTranscript, modifiedTranscript);

      if (!delta) {
        // Pas de changements
        return;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/recording/${recordingId}/transcript`,
        {
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
          body: JSON.stringify({ delta }),
        }
      );

      await handleErrorResponse(response, teamsContext);
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['recording-get', variables.recordingId],
      });
    },
  });
};

export const useRegenerateReportMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();

  return useMutation<
    { id: string },
    Record<string, unknown>,
    {
      recordingId: string;
      userContext?: string;
    }
  >({
    mutationKey: ['regenerate-report'],
    mutationFn: async ({ recordingId, userContext }) => {
      const response = await fetch(
        `${BACKEND_URL}/api/recording/${recordingId}/regenerate-report`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
          body: JSON.stringify({ userContext }),
        }
      );

      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['recording-get', variables.recordingId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['recording-list-get'],
      });
    },
  });
};

export const useUploadAudioMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, Error, { file: File }>({
    mutationKey: ['upload-audio'],
    mutationFn: async ({ file }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/recording/upload`, {
        method: 'POST',
        headers: {
          // Note: Do NOT set Content-Type for FormData, browser will set it with boundary
          Authorization: teamsContext.authCode
            ? `Bearer ${teamsContext.authCode}`
            : '',
        },
        body: formData,
      });

      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['recording-list-get'],
      });
    },
  });
};

export const useForceOneDriveSyncMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationKey: ['force-onedrive-sync'],
    mutationFn: async (recordingId: string) => {
      const response = await fetch(
        `${BACKEND_URL}/api/recording/${recordingId}/onedrive-sync`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
        }
      );

      await handleErrorResponse(response, teamsContext);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['recording-list-get'],
      });
    },
  });
};
